import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { openGoogleMapsNavigation } from '../utils/navigation';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Load Google Maps API once globally
let googleMapsLoading = false;
let googleMapsLoaded = false;
const googleMapsCallbacks = [];

const loadGoogleMapsAPI = (callback) => {
  if (googleMapsLoaded && window.google?.maps) {
    callback();
    return;
  }
  
  googleMapsCallbacks.push(callback);
  
  if (googleMapsLoading) return;
  googleMapsLoading = true;
  
  const script = document.createElement('script');
  script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,marker`;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    googleMapsLoaded = true;
    googleMapsLoading = false;
    googleMapsCallbacks.forEach(cb => cb());
    googleMapsCallbacks.length = 0;
  };
  script.onerror = () => {
    googleMapsLoading = false;
    console.error('Failed to load Google Maps API');
  };
  document.head.appendChild(script);
};

// Main Google Map component
export const GoogleMap = ({
  center = { lat: 50.8225, lng: -0.1372 },
  zoom = 12,
  markers = [],
  onMarkerClick,
  onMapClick,
  selectedMarkerId,
  height = '400px',
  draggableMarkers = false,
  onMarkerDragEnd,
  showControls = true,
  mapStyle = 'roadmap', // roadmap, satellite, hybrid, terrain
  className = '',
  children
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [isLoaded, setIsLoaded] = useState(false);

  // Load Google Maps
  useEffect(() => {
    loadGoogleMapsAPI(() => setIsLoaded(true));
  }, []);

  // Initialize map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom,
      mapTypeId: mapStyle,
      disableDefaultUI: !showControls,
      zoomControl: showControls,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    if (onMapClick) {
      map.addListener('click', (e) => {
        onMapClick({
          lat: e.latLng.lat(),
          lng: e.latLng.lng()
        });
      });
    }

    mapInstanceRef.current = map;
  }, [isLoaded, showControls, mapStyle]);

  // Update center
  useEffect(() => {
    if (mapInstanceRef.current && center) {
      mapInstanceRef.current.setCenter(center);
    }
  }, [center.lat, center.lng]);

  // Update zoom
  useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setZoom(zoom);
    }
  }, [zoom]);

  // Update markers
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Add new markers
    markers.forEach(markerData => {
      const isSelected = markerData.id === selectedMarkerId;
      
      const marker = new window.google.maps.Marker({
        position: { lat: markerData.lat, lng: markerData.lng },
        map: mapInstanceRef.current,
        title: markerData.title || '',
        draggable: draggableMarkers,
        icon: markerData.icon || {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: isSelected ? 12 : 10,
          fillColor: markerData.color || (isSelected ? '#059669' : '#10b981'),
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
        animation: isSelected ? window.google.maps.Animation.BOUNCE : null
      });

      // Stop animation after a short time
      if (isSelected) {
        setTimeout(() => marker.setAnimation(null), 1500);
      }

      if (markerData.infoContent) {
        const infoWindow = new window.google.maps.InfoWindow({
          content: markerData.infoContent
        });
        marker.addListener('click', () => {
          infoWindow.open(mapInstanceRef.current, marker);
          if (onMarkerClick) onMarkerClick(markerData);
        });
      } else if (onMarkerClick) {
        marker.addListener('click', () => onMarkerClick(markerData));
      }

      if (draggableMarkers && onMarkerDragEnd) {
        marker.addListener('dragend', (e) => {
          onMarkerDragEnd({
            ...markerData,
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
          });
        });
      }

      markersRef.current.push(marker);
    });

    // Fit bounds if multiple markers
    if (markers.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(m => bounds.extend({ lat: m.lat, lng: m.lng }));
      mapInstanceRef.current.fitBounds(bounds, 50);
    }
  }, [markers, isLoaded, selectedMarkerId, draggableMarkers, onMarkerClick, onMarkerDragEnd]);

  if (!isLoaded) {
    return (
      <div style={{ height }} className={`flex items-center justify-center bg-zinc-100 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div style={{ height }} className={`relative ${className}`}>
      <div ref={mapRef} className="w-full h-full rounded-inherit" />
      {children}
    </div>
  );
};

// Mini map preview component (non-interactive)
export const MiniGoogleMap = ({
  latitude,
  longitude,
  address,
  height = '150px',
  showNavigate = true,
  onClick,
  className = ''
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    loadGoogleMapsAPI(() => setIsLoaded(true));
  }, []);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !latitude || !longitude) return;
    if (mapInstanceRef.current) return;

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: latitude, lng: longitude },
      zoom: 15,
      disableDefaultUI: true,
      draggable: false,
      scrollwheel: false,
      disableDoubleClickZoom: true,
      styles: [
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] }
      ]
    });

    new window.google.maps.Marker({
      position: { lat: latitude, lng: longitude },
      map,
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 3,
      }
    });

    mapInstanceRef.current = map;
  }, [isLoaded, latitude, longitude]);

  const handleNavigate = (e) => {
    e.stopPropagation();
    openGoogleMapsNavigation(latitude, longitude, address);
  };

  if (!latitude || !longitude) {
    return (
      <div 
        style={{ height }}
        className={`bg-zinc-200 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:bg-zinc-300 transition-colors ${className}`}
        onClick={onClick}
      >
        <svg className="w-8 h-8 text-zinc-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p className="text-xs text-zinc-500 text-center px-4">
          {address ? address.split(',')[0] : 'No location set'}
        </p>
        <p className="text-xs text-zinc-400 mt-1">Click to set location</p>
      </div>
    );
  }

  return (
    <div style={{ height }} className={`relative rounded-xl overflow-hidden ${className}`}>
      {!isLoaded && (
        <div className="absolute inset-0 bg-zinc-200 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </div>
      )}
      <div 
        ref={mapRef}
        className="w-full h-full cursor-pointer"
        onClick={onClick}
      />
      {showNavigate && (
        <button
          onClick={handleNavigate}
          className="absolute bottom-3 left-3 w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center text-white shadow-lg hover:bg-orange-600 transition-colors"
          title="Navigate"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
          </svg>
        </button>
      )}
    </div>
  );
};

// Export a hook to access the map instance
export const useGoogleMaps = () => {
  const [isLoaded, setIsLoaded] = useState(googleMapsLoaded);
  
  useEffect(() => {
    if (googleMapsLoaded) {
      setIsLoaded(true);
    } else {
      loadGoogleMapsAPI(() => setIsLoaded(true));
    }
  }, []);

  return { isLoaded, google: isLoaded ? window.google : null };
};

export default GoogleMap;
